import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { COORDINATION_AGENDA } from "@/lib/basProcess/constants";
import { CalendarDays, Plus, Trash2 } from "lucide-react";
import { DocumentHeader } from "@/components/BasProcess/DocumentHeader";

interface Props {
  projectId: string;
}

interface Meeting {
  id: string;
  meeting_date: string;
  participants: string | null;
  content_json: Record<string, string>;
  status: string;
  stage_id: string | null;
}

export const CoordinationMeetings: React.FC<Props> = ({ projectId }) => {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [stages, setStages] = useState<{ id: string; name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = async () => {
    const { data, error } = await supabase
      .from("coordination_meetings")
      .select("id, meeting_date, participants, content_json, status, stage_id")
      .eq("project_id", projectId)
      .order("meeting_date", { ascending: false });
    if (error) toast.error("Kunde inte läsa mötena");
    else setMeetings((data || []).map((m: any) => ({ ...m, content_json: m.content_json || {} })));
    setLoading(false);
  };

  const loadStages = async () => {
    const { data } = await supabase
      .from("project_stages")
      .select("id, name")
      .eq("project_id", projectId)
      .order("created_at", { ascending: true });
    setStages(data || []);
  };

  useEffect(() => {
    setLoading(true);
    load();
    loadStages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId]);

  const create = async () => {
    const { data, error } = await supabase
      .from("coordination_meetings")
      .insert({ project_id: projectId })
      .select("id")
      .single();
    if (error) return toast.error("Kunde inte skapa mötet");
    await load();
    setOpenId(data.id);
  };

  const update = async (id: string, patch: Partial<Meeting>) => {
    setMeetings((prev) => prev.map((m) => (m.id === id ? { ...m, ...patch } : m)));
    const { error } = await supabase.from("coordination_meetings").update(patch as any).eq("id", id);
    if (error) toast.error("Kunde inte spara");
  };

  const updateField = (m: Meeting, key: string, value: string) =>
    update(m.id, { content_json: { ...m.content_json, [key]: value } });

  const remove = async (id: string) => {
    setMeetings((prev) => prev.filter((m) => m.id !== id));
    await supabase.from("coordination_meetings").delete().eq("id", id);
  };

  if (loading) return <div className="py-12 text-center text-muted-foreground">Laddar möten...</div>;

  return (
    <div className="space-y-4">
      <DocumentHeader
        docNumber="23"
        title="Samordningsmöten Bas-P / Bas-U"
        description="Protokoll över risker, ändringar och kvarstående punkter mellan projektering och utförande."
        icon={CalendarDays}
        actions={<Button onClick={create}><Plus className="h-4 w-4 mr-1" />Nytt möte</Button>}
      />

      {meetings.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center space-y-3">
            <CalendarDays className="h-8 w-8 mx-auto text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Inga samordningsmöten dokumenterade ännu.</p>
            <Button onClick={create}>Skapa första mötet</Button>
          </CardContent>
        </Card>
      ) : (
        meetings.map((m) => {
          const isOpen = openId === m.id;
          const filled = COORDINATION_AGENDA.filter((a) => (m.content_json?.[a.key] || "").trim()).length;
          return (
            <Card key={m.id}>
              <CardHeader className="pb-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <button className="text-left" onClick={() => setOpenId(isOpen ? null : m.id)}>
                    <CardTitle className="text-base">Möte {m.meeting_date}</CardTitle>
                    <p className="text-sm text-muted-foreground">
                      {m.participants || "Inga deltagare angivna"}
                    </p>
                  </button>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">{filled}/{COORDINATION_AGENDA.length} punkter</Badge>
                    <Button variant="ghost" size="icon" onClick={() => remove(m.id)}>
                      <Trash2 className="h-4 w-4 text-muted-foreground" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              {isOpen && (
                <CardContent className="space-y-4">
                  <div className="grid gap-2 sm:grid-cols-2">
                    <Input
                      type="date"
                      defaultValue={m.meeting_date}
                      onBlur={(e) => update(m.id, { meeting_date: e.target.value })}
                    />
                    <Input
                      placeholder="Deltagare (namn, roll)"
                      defaultValue={m.participants ?? ""}
                      onBlur={(e) => update(m.id, { participants: e.target.value })}
                    />
                  </div>
                  {stages.length > 0 && (
                    <div className="space-y-1">
                      <label className="text-sm font-medium">Etapp</label>
                      <Select
                        value={m.stage_id ?? "none"}
                        onValueChange={(v) => update(m.id, { stage_id: v === "none" ? null : v })}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Hela projektet" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Hela projektet</SelectItem>
                          {stages.map((s) => (
                            <SelectItem key={s.id} value={s.id}>
                              {s.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                  {COORDINATION_AGENDA.map((a) => (
                    <div key={a.key} className="space-y-1">
                      <label className="text-sm font-medium">{a.label}</label>
                      {a.hint && <p className="text-xs text-muted-foreground">{a.hint}</p>}
                      <Textarea
                        rows={2}
                        defaultValue={m.content_json?.[a.key] ?? ""}
                        onBlur={(e) => updateField(m, a.key, e.target.value)}
                      />
                    </div>
                  ))}
                </CardContent>
              )}
            </Card>
          );
        })
      )}
    </div>
  );
};

export default CoordinationMeetings;
